import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LoadingSection, showToast } from '@quickmd-llc/design-system'
import { MANAGEMENT_PAGE_ROUTE } from '../../../../utils/constants'
import IntakeFormViewMode from './IntakeFormViewMode/IntakeFormViewMode'
import { errorCodes, getUserFriendlyErrorMessage } from '../../../../utils/errormessages'
import { text } from '../Constants'
import { useIntakeForm } from '../hooks/useIntakeForm'
import { useErrorToast } from '@/hooks/useErrorToast'

/**
 * Route shell: reads ?id=, fires useIntakeForm, guards loading + not-found.
 *
 * Error path:
 *  - INTAKE_FORM_NOT_FOUND → DS showToast (type:'error', position:'bottom-right') with the
 *    same message the original <ErrorAlert> showed, then navigate-away to /management after
 *    3500ms (preserves original ViewIntakeForm.jsx timing exactly).
 *  - Other errors (generic/network query failures) → surfaced via useErrorToast, restoring
 *    the original toastErrorMessGenerator(error) behavior from ViewIntakeForm.jsx.
 *
 * Loader: replaced PageLoadingSpinner (ui_kit_qmd) → LoadingSection (DS), consistent with
 * the rest of wave 3 (MedicationsPage / ClinicMembers / management patterns).
 */

const notFoundMessage = getUserFriendlyErrorMessage(errorCodes.DOES_NOT_EXIST_NO_REFRESH, null, text.INTAKE_FORM)

const ViewIntakeForm = () => {
  const navigate = useNavigate()
  const params = new URLSearchParams(window.location.search)
  const id = params.get('id') ?? ''
  const { loading, error: queryError } = useIntakeForm(id)
  // saveError holds errors emitted by IntakeFormViewMode (save / validation failures).
  // queryError drives the not-found navigate-away; saveError is passed back down for
  // CreateEditIntakeView to display inline.
  const [saveError, setSaveError] = useState('')

  // Combined error: query errors take precedence over local save errors.
  const error = queryError || saveError

  const setError = useCallback((msg: string) => setSaveError(msg), [])

  const toastError = useErrorToast()

  // Gate: fire the not-found toast exactly once per occurrence (no re-render spam).
  const notFoundToastedRef = useRef(false)

  useEffect(() => {
    if (!error) {
      notFoundToastedRef.current = false
      return
    }
    // Navigate away when the form is not found — preserves original 3500ms timer behavior.
    // Also fires DS showToast with the same message the original <ErrorAlert> displayed.
    if (error === notFoundMessage) {
      if (!notFoundToastedRef.current) {
        notFoundToastedRef.current = true
        showToast({ type: 'error', message: notFoundMessage, position: 'bottom-right' })
      }
      const timer = setTimeout(() => {
        setSaveError('')
        navigate(MANAGEMENT_PAGE_ROUTE, { replace: true })
      }, 3500)
      return () => clearTimeout(timer)
    }
    // Non-not-found errors (generic/network query failures) — surface via toast.
    // Restores original ViewIntakeForm.jsx toastErrorMessGenerator(error) behavior.
    // Mirrors IntakeFormsTableBody.tsx's useErrorToast pattern exactly.
    // toastError is a stable useCallback — no infinite-loop risk.
    toastError(error)
  }, [error, navigate, toastError])

  return (
    <div className='flex flex-1 flex-col'>
      {loading ?
        <LoadingSection />
      : <IntakeFormViewMode id={id} error={error} setError={setError} />}
    </div>
  )
}

export default ViewIntakeForm
